import { zodResolver } from '@hookform/resolvers/zod';
import {
  CONSUMER_ENVIRONMENT_LABELS,
  updateConsumerSchema,
  type Consumer,
  type UpdateConsumerInput,
} from '@easyhelp/shared';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Controller, useForm } from 'react-hook-form';
import { CommittentiField } from './CommittentiField';

export interface ConsumerEditDialogProps {
  open: boolean;
  consumer: Consumer;
  error?: string | null;
  busy?: boolean;
  onSubmit: (values: UpdateConsumerInput) => void;
  onClose: () => void;
}

/** Modifica di nome e committenti autorizzati; prodotto e ambiente sono mostrati ma immutabili (DEC-006 §5). */
export function ConsumerEditDialog({
  open,
  consumer,
  error = null,
  busy = false,
  onSubmit,
  onClose,
}: ConsumerEditDialogProps) {
  const { control, handleSubmit } = useForm<UpdateConsumerInput>({
    resolver: zodResolver(updateConsumerSchema),
    defaultValues: { name: consumer.name, authorizedCommittenti: consumer.authorizedCommittenti },
  });

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="consumer-edit-title"
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogTitle id="consumer-edit-title">Modifica {consumer.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={4} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {consumer.product.name} ({consumer.product.prefix}) · {CONSUMER_ENVIRONMENT_LABELS[consumer.environment]}.
              Prodotto e ambiente non si cambiano: per un'altra installazione registra un nuovo consumer.
            </Typography>
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  label="Nome"
                  required
                  autoFocus
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="authorizedCommittenti"
              control={control}
              render={({ field, fieldState }) => (
                <CommittentiField
                  value={field.value ?? []}
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                />
              )}
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>
            Annulla
          </Button>
          <Button type="submit" variant="contained" disabled={busy}>
            Salva
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
